import { supabase } from "@/lib/supabase";
import type { User, AuthError } from "@supabase/supabase-js";

export interface SignUpData {
	email: string;
	password: string;
	fullName: string;
	role: "parent" | "doula";
}

export interface SignInData {
	email: string;
	password: string;
}

export class AuthService {
	static async signUp(
		data: SignUpData,
	): Promise<{ user: User | null; error: AuthError | null }> {
		try {
			// Call the edge function for user creation
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user-no-confirmation`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						email: data.email,
						password: data.password,
						fullName: data.fullName,
						role: data.role,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				return {
					user: null,
					error: new Error(result.error || "User creation failed") as AuthError,
				};
			}

			// Sign in the user after successful creation
			const { data: authData, error: signInError } =
				await supabase.auth.signInWithPassword({
					email: data.email,
					password: data.password,
				});

			if (signInError) {
				return { user: null, error: signInError };
			}

			return { user: authData.user, error: null };
		} catch (error) {
			console.error("SignUp error:", error);
			return { user: null, error: error as AuthError };
		}
	}

	static async signIn(
		data: SignInData,
	): Promise<{ user: User | null; error: AuthError | null }> {
		try {
			const { data: authData, error } = await supabase.auth.signInWithPassword({
				email: data.email,
				password: data.password,
			});

			if (error) return { user: null, error };

			// Get user role and profile data using edge function
			if (authData.user) {
				try {
					const roleResponse = await fetch(
						`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-role`,
						{
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
							},
							body: JSON.stringify({
								authUserId: authData.user.id,
							}),
						},
					);

					const roleResult = await roleResponse.json();

					if (roleResponse.ok && roleResult.profile) {
						// Update user metadata with role information
						await supabase.auth.updateUser({
							data: {
								role: roleResult.profile.role,
								onboarding_completed: roleResult.profile.onboarding_completed,
								onboarding_step: roleResult.profile.onboarding_step,
							},
						});
					} else {
						console.error("❌ AuthService: get-user-role failed:", roleResult);
					}
				} catch (roleError) {
					console.error("Error fetching user role:", roleError);
					// Continue with sign in even if role fetch fails
				}
			}

			return { user: authData.user, error: null };
		} catch (error) {
			console.error("SignIn error:", error);
			return { user: null, error: error as AuthError };
		}
	}

	static async signOut(): Promise<{ error: AuthError | null }> {
		const { error } = await supabase.auth.signOut();
		return { error };
	}

	static async getCurrentUser(): Promise<User | null> {
		const {
			data: { user },
		} = await supabase.auth.getUser();
		return user;
	}

	static async getUserProfile(userId: string) {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-role`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						authUserId: userId,
					}),
				},
			);

			const result = await response.json();

			if (response.ok && result.profile) {
				return { data: result.profile, error: null };
			} else {
				return {
					data: null,
					error: new Error(result.error || "Failed to fetch profile"),
				};
			}
		} catch (error) {
			console.error("GetUserProfile error:", error);
			return { data: null, error };
		}
	}

	static async refreshUserRole(userId: string) {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-role`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						authUserId: userId,
					}),
				},
			);

			const result = await response.json();

			if (response.ok && result.profile) {
				// Update user metadata with latest role information
				await supabase.auth.updateUser({
					data: {
						role: result.profile.role,
						onboarding_completed: result.profile.onboarding_completed,
						onboarding_step: result.profile.onboarding_step,
					},
				});

				return { data: result.profile, error: null };
			} else {
				return {
					data: null,
					error: new Error(result.error || "Failed to refresh user role"),
				};
			}
		} catch (error) {
			console.error("RefreshUserRole error:", error);
			return { data: null, error };
		}
	}
}
